import type {
  CfoContractMeta,
  CfoContractReadiness,
  InsightDrillTarget,
  InsightSeverity,
} from '../../../../data/ServerApiClient'

/** Canonical CEO Cockpit HTTP contract version — mirrors Rust `CEO_COCKPIT_CONTRACT_VERSION`. */
export const CEO_COCKPIT_CONTRACT_VERSION = '2026-08-ceo-cockpit-v1'
export const DEFAULT_OWNER_DEMO_SLUG = 'alder-ridge-outfitters'

/*
 * Nullability mirrors `server/src/integrations/quickbooks/ceo_cockpit/contract.rs`
 * field-for-field:
 *   • Rust `Option<T>` WITHOUT `skip_serializing_if`  → `T | null` (key always present, may be null)
 *   • Rust `Option<T>` WITH `skip_serializing_if`     → `field?: T | null` (key absent when None)
 *   • Rust `T`                                        → `T`
 * Consumers must treat every `null`/absent as "Not prepared", never as zero.
 */

/** Rust `CeoTrendPoint` — `value` is `Option<f64>` serialized as `null` (not skipped). */
export type CeoTrendPoint = { period: string; value: number | null }

export type CeoPeriodKind = 'mtd' | 'qtd' | 'ytd'
export type CeoBasisKind = 'plan' | 'forecast' | 'prior_year' | 'prior_period'
export type CeoMetricId =
  | 'sales'
  | 'revenue'
  | 'gross_profit'
  | 'operating_income'
  | 'net_income'
  | 'cash'
  | 'accounts_receivable'
  | 'mrr'
export type CeoScorecardKind = 'flow' | 'point_in_time'
export type CeoPolarity = 'higher_is_better' | 'lower_is_better' | 'contextual'
export type CeoScorecardBasis = 'sales_fact' | 'gl_resolved' | 'balance' | 'native_statements'
export type CeoPaceStatus = 'ahead' | 'on_track' | 'behind' | 'unknown' | 'insufficient'
export type CeoDimensionKind =
  | 'location'
  | 'class'
  | 'sales_rep'
  | 'employee'
  | 'customer_type'
  | 'channel'
  | 'item'
  | 'item_category'
  | 'customer'
/**
 * Three states that must never be conflated:
 * - `available` / `partial` — data exists, render it.
 * - `not_applicable` — this business CANNOT have the thing (a law firm has no
 *   inventory, a restaurant has no billable utilization). Render NOTHING: no
 *   heading, no empty state, no invitation. Optional panels arrive as `null`.
 * - `not_tracked` — QuickBooks could carry it but this company does not tag it.
 *   Keep the inviting "not tracked yet — here's how to feed it" state.
 */
export type CeoAvailabilityStatus = 'available' | 'partial' | 'not_tracked' | 'not_applicable'

/** True when the section must render nothing at all. */
export function isHiddenAvailability(status: CeoAvailabilityStatus | null | undefined): boolean {
  return status === 'not_applicable'
}
export type CeoConnectTarget =
  | 'quickbooks_locations'
  | 'quickbooks_classes'
  | 'sales_rep_field'
  | 'customer_types'
  | 'items'
  | 'pos'
  | 'crm'
  | 'payroll'
  | 'inventory'
  | 'billing'
  | null
export type CeoDecisionKind = 'decide' | 'watch' | 'celebrate' | 'note'
export type CeoEvidenceUnit = 'usd' | 'pct' | 'days' | 'count' | 'months'
export type CeoCockpitStatusState = 'ready' | 'stale' | 'building' | 'missing' | 'disabled'
export type CeoIndustryKey = 'hvac' | 'restaurant' | 'law' | 'saas' | 'distributor'

export type CeoDateWindow = {
  start: string
  end: string
}

export type CeoPeriodWindow = {
  label: string
  current: CeoDateWindow
  period_end: string
  days_elapsed: number
  days_in_period: number
  business_days_elapsed: number
  business_days_in_period: number
  calendar_fraction: number
  is_complete: boolean
  prior_period: CeoDateWindow
  prior_period_full: CeoDateWindow
  prior_year: CeoDateWindow
  prior_year_full: CeoDateWindow
}

export type CeoReference = {
  full_period: number
  expected_to_date: number
  variance_abs?: number | null
  variance_pct?: number | null
  source: string
}

export type CeoPaceResult = {
  expected_to_date?: number | null
  variance_to_date?: number | null
  pace_pct?: number | null
  projection?: number | null
  /** How the low/high band was produced. `heuristic` widens with how little
   * of the period has elapsed and carries no empirical coverage — the UI must
   * call it a "rough pace range", never a confidence interval. `calibrated`
   * means it was backtested against this company's own forecast error. */
  interval_method?: string | null
  projection_low?: number | null
  projection_high?: number | null
  naive_run_rate?: number | null
  status: CeoPaceStatus
  confidence: number
  method: string
}

export type CeoComparisonBlock = {
  value?: number | null
  delta_abs?: number | null
  delta_pct?: number | null
  window: CeoDateWindow
}

export type CeoPeriodBlock = {
  actual?: number | null
  prior_period: CeoComparisonBlock
  prior_year: CeoComparisonBlock
  /**
   * Balance metrics only (`kind === 'point_in_time'`): the same balance at the
   * last CLOSED period end, with `window.end` carrying that close date. Plan
   * and forecast are always absent on balances — prorating a target against a
   * balance is meaningless — so tiles compare against this instead.
   */
  prior_close?: CeoComparisonBlock | null
  prior_period_full?: number | null
  prior_year_full?: number | null
  plan?: CeoReference | null
  forecast?: CeoReference | null
  pace?: CeoPaceResult | null
  complete: boolean
  provisional: boolean
}

export type CeoScorecardMetric = {
  id: CeoMetricId
  label: string
  kind: CeoScorecardKind
  polarity: CeoPolarity
  basis: CeoScorecardBasis
  mtd: CeoPeriodBlock
  qtd: CeoPeriodBlock
  ytd: CeoPeriodBlock
  /** Rust `Vec<CeoTrendPoint>`; use `trendValues()` to read numbers. */
  trend_12m: CeoTrendPoint[]
  drill?: InsightDrillTarget | null
}

export type CeoPaceDay = {
  date: string
  /** 1-based (day 1 = period start); `day_index === days_elapsed` is the as-of day. */
  day_index: number
  actual_cum?: number | null
  prior_year_cum?: number | null
  plan_cum?: number | null
  forecast_cum?: number | null
  is_future: boolean
}

export type CeoPaceCurve = {
  metric_id: CeoMetricId
  period: CeoPeriodKind
  days: CeoPaceDay[]
  projection: { end?: number | null; low?: number | null; high?: number | null }
}

export type CeoDimensionAvailability = {
  status: CeoAvailabilityStatus
  coverage_pct: number
  distinct_values: number
  entity_count?: number | null
  mapping_source: string
  /** Rust `Option<String>` (skipped when None → key absent). */
  connect_target?: CeoConnectTarget
  hint?: string | null
  reason?: string | null
}

export type CeoDimensionRow = {
  key: string
  label: string
  mtd: CeoPeriodBlock
  qtd: CeoPeriodBlock
  ytd: CeoPeriodBlock
  share_ytd: number
  rank: number
  prior_rank?: number | null
  delta_rank?: number | null
  gross_margin_pct_ytd?: number | null
  units_ytd?: number | null
  drill?: InsightDrillTarget | null
}

export type CeoMover = {
  key: string
  label: string
  delta_abs: number
  /** Rust `Option<f64>` (not skipped) — null when the baseline is zero/absent. */
  delta_pct: number | null
}

export type CeoDimension = {
  kind: CeoDimensionKind
  label: string
  availability: CeoDimensionAvailability
  rows: CeoDimensionRow[]
  totals: CeoDimensionRow
  movers: { up: CeoMover[]; down: CeoMover[] }
  target_source?: string | null
  gl_column?: string | null
}

export type CeoAtRiskCustomer = {
  customer_id: string
  customer: string
  open_ar: number
  overdue_ar: number
  over_60_ar: number
  risk_score: number
  ttm_sales: number
  share_of_sales: number
}

/** Rust `CeoMovementBridge` — every field is a plain `f64`. */
export type CeoRevenueMovement = {
  start: number
  new: number
  expansion: number
  contraction: number
  churn: number
  reactivation: number
  end: number
  reconciles: boolean
}

export type CeoCustomers = {
  top: CeoDimensionRow[]
  concentration: {
    top1_share: number
    top5_share: number
    top10_share: number
    hhi: number
  }
  new_logos: { mtd: number; qtd: number; ytd: number }
  /** Rust `Option<CeoMovementBridge>` (not skipped) — null until the bridge is prepared. */
  movement: CeoRevenueMovement | null
  at_risk: CeoAtRiskCustomer[]
  ar_total?: number | null
  dso?: number | null
  dso_delta?: number | null
}

export type CeoCohortCell = {
  age_months: number
  retained_share?: number | null
  retained_mrr?: number | null
  observable: boolean
  months_matured: number
}

export type CeoCohortRow = {
  cohort_month: string
  size: number
  cells: CeoCohortCell[]
}

export type CeoSaas = {
  recurring_share: number
  mrr: number
  arr: number
  /** Rust `Option<f64>` (not skipped) — null when there are no active customers. */
  arpa: number | null
  active_customers: number
  bridge: CeoRevenueMovement
  /** Rust `Option<f64>` (not skipped). */
  logo_churn_pct: number | null
  /** Rust `Option<f64>` (not skipped). */
  grr: number | null
  /** Rust `Option<f64>` (not skipped). */
  nrr: number | null
  cac?: number | null
  ltv?: number | null
  ltv_cac?: number | null
  payback_months?: number | null
  cohorts: {
    ages: number[]
    rows: CeoCohortRow[]
    readable: boolean
  }
  by_segment: CeoDimensionRow[]
  by_channel: CeoDimensionRow[]
  /** Rust `serde_json::Value` — free-form reason codes; never assume a shape. */
  reasons: Record<string, unknown>
}

export type CeoInventorySku = {
  item_id: string
  item_name: string
  /** Rust `Option<String>` (skipped when None). */
  sku?: string | null
  /** Rust `Option<String>` (skipped when None). */
  category?: string | null
  location?: string | null
  qty_on_hand: number
  unit_cost: number
  on_hand_value: number
  units_30d: number
  revenue_30d: number
  margin_30d: number
  /** Rust `Option<f64>` (not skipped) — null when no sell price is known. */
  unit_margin: number | null
  daily_velocity: number
  days_of_supply?: number | null
  /** Rust `Option<f64>` (not skipped). */
  sell_through_30d: number | null
  stockout_risk: boolean
  overstock: boolean
  stockout_days_30d: number
}

export type CeoInventory = {
  on_hand_units: number
  on_hand_value: number
  days_of_supply?: number | null
  turns?: number | null
  stockout_count: number
  skus: CeoInventorySku[]
  top_by_revenue: CeoInventorySku[]
  top_by_margin: CeoInventorySku[]
  bottom_by_margin: CeoInventorySku[]
  stockout_risk: CeoInventorySku[]
  overstock: CeoInventorySku[]
  hero_skus: CeoInventorySku[]
  by_category: CeoDimensionRow[]
  by_location: CeoDimensionRow[]
}

export type CeoLaborHeadcount = {
  key: string
  label: string
  count: number
  /** Rust `Vec<CeoTrendPoint>` — `[{period,value}]`, NOT `number[]`; read via `trendValues()`. */
  series_13m: CeoTrendPoint[]
}

export type CeoLaborPctRow = {
  key: string
  label: string
  /** Rust `Option<f64>` (not skipped) — null when sales for the window are zero/unknown. */
  mtd: number | null
  qtd: number | null
  ytd: number | null
  ttm: number | null
  target?: number | null
}

export type CeoUtilizationRow = {
  key: string
  label: string
  /** Rust `Option<f64>` (not skipped). */
  utilization: number | null
}

export type CeoUtilization = {
  /** Rust `Option<f64>` (not skipped). */
  overall: number | null
  by_employee: CeoUtilizationRow[]
}

export type CeoLabor = {
  headcount: number
  headcount_by_department: CeoLaborHeadcount[]
  payroll_by_department: CeoDimensionRow[]
  labor_pct_of_sales_by_location: CeoLaborPctRow[]
  revenue_per_employee?: number | null
  utilization?: CeoUtilization | null
  /** `not_applicable` → render no utilization tile at all. */
  utilization_availability: CeoAvailabilityStatus
  /** `not_applicable` → render no labor-%-of-sales section at all. */
  labor_pct_availability: CeoAvailabilityStatus
  availability: CeoDimensionAvailability
}

export type CeoCash = {
  cash_at_close?: number | null
  close_period?: string | null
  cash_estimated_as_of?: number | null
  estimated: boolean
  ar_open?: number | null
  dso?: number | null
  dso_delta?: number | null
  /** Absent — never `0` — when the company is not burning cash. */
  burn_avg_3m?: number | null
  /** Only for a company that IS burning. Otherwise read `months_of_cash`. */
  runway_months?: number | null
  /**
   * Cash ÷ trailing-3-month average monthly operating outflow: "months of cash
   * on hand at the current spend rate". Present exactly when `runway_months`
   * is absent and the balance is positive — for a profitable company runway is
   * not a risk metric, so the tile swaps rather than reading "Not prepared".
   */
  months_of_cash?: number | null
  monthly_outflow_avg_3m?: number | null
  min_projected_cash_13w?: number | null
  first_negative_week?: string | null
  min_cash_floor?: number | null
  /** Where the floor came from, e.g. "one payroll cycle plus fixed operating costs". */
  min_cash_floor_basis?: string | null
  /** Rust `Vec<CeoTrendPoint>`; read via `trendValues()`. */
  series_13w: CeoTrendPoint[]
  /** Rust `Option<serde_json::Value>` (skipped when None) — shape is not guaranteed. */
  trough?: { week?: string | null; amount?: number | null } | null
}

export type CeoDecisionEvidence = {
  label: string
  value: number
  unit: CeoEvidenceUnit
  display: string
  delta_display?: string | null
  /** Rust `Option<String>` — usually an `InsightSeverity`, but never switch on it exhaustively. */
  tone?: InsightSeverity | string | null
  metric_ref?: string | null
}

export type CeoDecision = {
  id: string
  rule: string
  kind: CeoDecisionKind
  severity: InsightSeverity
  headline: string
  body: string
  evidence: CeoDecisionEvidence[]
  metric_refs: string[]
  drill: InsightDrillTarget
  score: number
  rank: number
  tags: string[]
  owner_hint?: string | null
}

export type CeoNarrative = {
  headline: string
  paragraphs: string[]
  cited_card_ids: string[]
  model_id: string
  generated_at: string
}

export type CeoFreshness = {
  /** Rust `Option<DateTime<Utc>>` (skipped when None) — absent before the first sync lands. */
  latest_sync_at?: string | null
  sales_data_through?: string | null
  gl_data_through?: string | null
  latest_closed_period?: string | null
  next_refresh_at?: string | null
  is_stale: boolean
  stale_reason?: string | null
}

export type CeoBasisAvailability = {
  available: boolean
  hint?: string | null
}

export type CeoCompany = {
  name: string
  currency_code: string
  fiscal_year_start_month: number
  industry_key: CeoIndustryKey | string
  industry_label: string
  is_demo: boolean
  demo_slug?: string | null
}

export type CeoCockpitStatus = {
  state: CeoCockpitStatusState
  snapshot_id?: string | null
  built_at?: string | null
  build_ms?: number | null
  source_fingerprint?: string | null
  rebuild_scheduled: boolean
  last_error?: string | null
  review_mode?: 'current_books_as_of' | null
  source_data_through?: string | null
}

export type CeoCalendar = {
  fy_start_month: number
  fiscal_year_label: string
  mtd: CeoPeriodWindow
  qtd: CeoPeriodWindow
  ytd: CeoPeriodWindow
  proration_method: string
  profile_source_days: number
}

export type CeoGoalCheckIn = {
  actual_value?: number | null
  period_end?: string | null
  status?: string | null
  performance_status?: string | null
}

export type CeoGoal = {
  id?: string
  goal_key: string
  title: string
  purpose?: string | null
  status: string
  cadence?: string | null
  metric_key?: string | null
  target_value?: number | null
  target_min_value?: number | null
  target_max_value?: number | null
  latest_actual?: CeoGoalCheckIn | null
  latest_check_in?: CeoGoalCheckIn | null
}

export type CeoGoalAction = {
  id: string
  title: string
  description?: string | null
  status: string
  due_on?: string | null
}

/**
 * `goal_scorecard_for_workbook` payload attached at read time. Rust ships it as
 * `Option<serde_json::Value>` (skipped when None), so nothing here is guaranteed —
 * always `Array.isArray(goals)` before iterating.
 */
export type CeoGoalScorecard = {
  workspace_key?: string
  role?: string
  goals?: CeoGoal[] | null
  actions?: CeoGoalAction[] | null
}


/** Which horizon a finding belongs to. It belongs to exactly one. */
export type CeoCadence = 'today' | 'week' | 'month'

export type CeoIncidentFamily =
  | 'sales'
  | 'supply'
  | 'labor'
  | 'collections'
  | 'cash'
  | 'retention'
  | 'unit_economics'
  | 'margin'
  | 'data_quality'

export type CeoIncidentState = 'new' | 'worsening' | 'unchanged' | 'recovering' | 'resolved'


/** A card's visual: the claim in its second form, chosen and populated by the
 *  server from the same panel rows the copy was written from. */
export type CeoVisualUnit = 'usd' | 'pct' | 'count' | 'days'

export type CeoCardVisual =
  | { kind: 'pace_curve'; metric_id: string; period: string; caption: string }
  | { kind: 'aging_stack'; rows: Array<{ label: string; current: number; overdue: number; over_60: number }>; caption: string }
  | {
      kind: 'cover_bars'
      rows: Array<{ label: string; category?: string | null; days_of_supply?: number | null; out_of_stock: boolean; detail: string }>
      threshold_days: number
      mode: 'stockout' | 'overstock'
      caption: string
    }
  | {
      kind: 'target_bullets'
      rows: Array<{ label: string; value: number; target?: number | null; context?: number | null }>
      unit: CeoVisualUnit
      caption: string
    }
  | { kind: 'weeks_bars';
      unit?: 'usd' | 'pct' | 'count' | 'days' | 'hours'; weeks: Array<{ label: string; value: number; complete: boolean }>; median?: number | null; caption: string }
  | {
      kind: 'margin_trend'
      months: Array<{ period: string; label: string; value: number }>
      usual: number
      highlight_period?: string | null
      caption: string
    }
  | { kind: 'pareto'; rows: Array<{ label: string; share: number }>; caption: string }
  | { kind: 'payback'; months: number; comfort_months: number; cac?: number | null; ltv_multiple?: number | null; caption: string }
  | { kind: 'cash_position'; now?: number | null; floor?: number | null; low_13w?: number | null; weeks_ahead: number[]; caption: string }
  | { kind: 'ranked_bars'; rows: Array<{ label: string; value: number; sublabel?: string | null }>; unit: CeoVisualUnit; caption: string }
  | { kind: 'cohort_grid'; caption: string }

export type CeoCadenceItem = {
  key: string
  cadence: CeoCadence
  family: CeoIncidentFamily
  kind: CeoDecisionKind
  headline: string
  why: string
  next_step: string
  owner?: string | null
  exposure_label: string
  exposure_value?: number | null
  fact_ids: string[]
  state: CeoIncidentState
  /** The story this item tells; one story never appears on two horizons. */
  story: string
  /** The claim in its second form. A card without one is a defect. */
  visual?: CeoCardVisual | null
}

export type CeoCadences = {
  today: CeoCadenceItem[]
  week: CeoCadenceItem[]
  month: CeoCadenceItem[]
  /**
   * Findings this rhythm produced that are already being told on a more urgent
   * horizon. Without it a quiet week is ambiguous — "nothing is drifting"
   * reads as calm books when the truth may be "the drift is on Today".
   */
  week_already_shown?: number
  month_already_shown?: number
}

/** A problem a person can own, rather than a metric that crossed a line. */
export type CeoIncident = {
  incident_key: string
  family: CeoIncidentFamily
  kind: CeoDecisionKind
  headline: string
  why: string
  next_step: string
  exposure_value?: number | null
  exposure_label: string
  owner_hint?: string | null
  state: CeoIncidentState
  first_seen_on: string
  observations: number
  fact_ids: string[]
  signals: CeoDecision[]
  score: number
  rank: number
}

export type CeoSinceYesterday = {
  new: number
  worsening: number
  unchanged: number
  recovering: number
  resolved: number
}

/** What a quiet morning is allowed to claim it looked at. */
export type CeoCheckedScope = {
  areas: number
  area_labels: string[]
  through: string
}

export type CeoWeekPoint = {
  week_start: string
  week_end: string
  /** False for the week containing the cutoff; trends must read only complete weeks. */
  complete: boolean
  label: string
  sales?: number | null
  gross_profit?: number | null
  labor?: number | null
  labor_pct_of_sales?: number | null
  /** Customers whose first-ever purchase landed in this week. */
  new_customers?: number | null
  /** Billable hours logged this week, when the business tracks time. */
  billable_hours?: number | null
}

export type CeoWeekly = {
  weeks: CeoWeekPoint[]
  complete_weeks: number
  /** False when payroll posts fortnightly or monthly, so no weekly labor ratio is drawn. */
  labor_is_weekly: boolean
}

/** What this company named as its outcome, its lever and its constraint. */
export type CeoCriticalInputs = {
  outcome_metric?: string | null
  outcome_target?: number | null
  outcome_actual?: number | null
  outcome_label?: string | null
  input_metric_key?: string | null
  input_label?: string | null
  input_unit?: string | null
  input_target?: number | null
  input_actual?: number | null
  input_recorded_on?: string | null
  /** How many of the last seven days carry a value. A lever nobody is pulling
   *  is worth saying out loud. */
  input_days_recorded_last_7: number
  constraint_note: string
  constraint_metric?: string | null
  materiality_usd?: number | null
  materiality_pct?: number | null
  /** False when nothing has been configured, so the page offers setup rather
   *  than rendering an empty frame. */
  configured: boolean
}

/** Where a number came from. A derived value must never masquerade as a
 *  decision the reader made. */
export type CeoPlanOrigin = 'derived' | 'configured' | 'unavailable'

export type CeoDerivedValue = {
  value?: number | null
  origin: CeoPlanOrigin
  /** The sentence shown when a reader asks where this came from. */
  basis: string
}

/** Targets the books set for themselves, so the page works before anyone has
 *  typed anything. */
export type CeoDerivedPlan = {
  outcome_target: CeoDerivedValue
  materiality_usd: CeoDerivedValue
  labor_pct_target: CeoDerivedValue
}

/** Monthly native authority. Unsupported operating families are explicit. */
export type CeoCurrentSource = {
  contract: 'fg.ceo.current_native.v1'
  source_revision: number
  source_checkpoint: string
  source_job_id: string
  accounting_basis: 'accrual' | 'cash'
  currency_code: string
  period_start: string
  period_end: string
  completeness: 'complete_month' | 'partial_month'
  general_ledger_verified: false
  exact_values: Record<string, string>
  availability: Record<string, { available: boolean; hint?: string | null }>
}

export type CeoCockpitResponse = {
  current_source?: CeoCurrentSource | null
  workbook_id: string
  connection_id: string
  realm_id: string
  company: CeoCompany
  contract: CfoContractMeta
  status: CeoCockpitStatus
  as_of_date: string
  freshness: CeoFreshness
  calendar: CeoCalendar
  basis_availability: Record<CeoBasisKind, CeoBasisAvailability>
  scorecard: CeoScorecardMetric[]
  pace_curves: CeoPaceCurve[]
  dimensions: CeoDimension[]
  customers: CeoCustomers | null
  saas?: CeoSaas | null
  inventory?: CeoInventory | null
  labor?: CeoLabor | null
  cash: CeoCash
  decisions: CeoDecision[]
  attention_count: number
  goals?: CeoGoalScorecard | null
  narrative?: CeoNarrative | null
  incidents?: CeoIncident[]
  held_back_count?: number
  since_yesterday?: CeoSinceYesterday | null
  checked_scope?: CeoCheckedScope | null
  weekly?: CeoWeekly | null
  cadences?: CeoCadences | null
  critical_inputs?: CeoCriticalInputs | null
  derived_plan?: CeoDerivedPlan | null
  warnings: string[]
  telemetry: {
    build_ms: number
    engine_ms: Record<string, number>
    rows: Record<string, number>
  }
}

export type CeoCockpitSummary = {
  current_source?: CeoCurrentSource | null
  workbook_id: string
  as_of_date: string
  sales_mtd?: number | null
  sales_mtd_vs_py_pct?: number | null
  sales_pace_status?: CeoPaceStatus | null
  plan_variance_pct?: number | null
  projection_mtd?: number | null
  top_decision?: string | null
  attention_count: number
  cash_runway_months?: number | null
  saas_mrr?: number | null
  /** Rust `DateTime<Utc>` — always present on a summary. */
  built_at: string
}

export type CeoCockpitSnapshotAvailability = {
  snapshot_id: string
  as_of_date: string
  built_at: string
}

export type CeoCockpitSnapshotIndex = {
  current_source?: { available_period_ends: string[] }
  latest_date: string | null
  retention_days: number
  snapshots: CeoCockpitSnapshotAvailability[]
}

export type CeoCockpitPeriodRun = {
  run_id: string
  workbook_id: string
  as_of_date: string
  source_data_through: string
  status: 'queued' | 'running' | 'ready' | 'failed'
  stage: 'queued' | 'loading_books' | 'building_period' | 'validating' | 'saving' | 'complete' | 'failed' | string
  progress_pct: number
  message: string
  snapshot_id?: string | null
  error?: string | null
  created_at: string
  updated_at: string
}

export type CeoCockpitSettings = {
  workbook_id?: string
  connection_id?: string | null
  location_field: 'auto' | 'department' | 'class' | 'none'
  channel_field: 'auto' | 'class' | 'customer_type' | 'doc_type' | 'custom_field' | 'none'
  channel_custom_field_name?: string | null
  customer_type_field: 'customer_type' | 'class' | 'none'
  sales_rep_field: 'auto' | 'sales_rep_ref' | 'custom_field' | 'employee_ref' | 'none'
  sales_rep_custom_field_name?: string
  holiday_calendar: 'none' | 'us_federal'
  recurring_item_ids?: unknown
  recurring_item_keywords?: unknown
  recurring_revenue_min_share?: number
  sales_marketing_account_ids?: unknown
  sales_marketing_class_ids?: unknown
  payroll_account_ids?: unknown
  labor_pct_target?: number | null
  lead_time_days?: number
  overstock_days?: number
  narrative_enabled?: boolean
  settings_json?: Record<string, unknown>
  updated_by?: string | null
  created_at?: string
  updated_at?: string
}

export type DemoCompany = {
  slug: string
  name: string
  industry_key: CeoIndustryKey
  industry_label: string
  tagline: string
  site_slug: string
  workbook_id: string
  accounting_provider: string
  ceo_headline?: string | null
}

export type DemoCompaniesResponse = {
  companies: DemoCompany[]
}

export type { CfoContractMeta, CfoContractReadiness, InsightDrillTarget, InsightSeverity }
